import { useMemo } from 'react';

import { getESFieldDisplayName } from 'modules/workflows/utils';
import { BaseOption } from 'ui/selects/types';
import { BRAND_AFFINITY } from 'utils/fieldConstants';
import ResourceSelect from '../ResourceSelect/ResourceSelect';
import { useFieldMappingOptions } from './useFieldMappingOptions';

export type ResourceFieldSelectProps = {
  nodeData: {
    field?: string;
  };
  id?: string;
  placeholder?: string;
  dataType?: string;
  /** TODO: update `any` type */
  onChange: (val: any) => void;
  /** Display value to use from cloned data */
  displayValue?: string;
  isError?: boolean;
  isStartNode?: boolean;
  isDisabled?: boolean;
  /** set tooltip content if disabled */
  setTooltipContent?: (content: string) => void;
};

type AttributeSplitResourceFieldSelectProps = ResourceFieldSelectProps & {
  isLoadingMapping: boolean;
  fieldOptions: BaseOption<string>[];
};

/**
 * Options to exclude in the trigger node
 * See:
 *  - https://iterable.atlassian.net/browse/WF-3771
 *  - https://iterable.atlassian.net/browse/WF-4942
 */
const excludedOptions = [
  'ESFields.Email',
  'ESFields.SignupSourceField',
  'ESFields.SignupDate',
  'ESFields.EmailListIds',
  'ESFields.UserListIds',
  'ESFields.UnsubscribedChannelIds',
  'ESFields.UnsubscribedMessageTypeIds',
  'ESFields.SubscribedMessageTypeIds',
  'ESFields.ProfileUpdatedAt',
  'ESFields.ItblInternalObject',
  'ESFields.ItblDSObject',
  BRAND_AFFINITY,
];

export const AttributeSplitResourceFieldSelect = ({
  nodeData = {},
  dataType,
  displayValue,
  isLoadingMapping = false,
  fieldOptions = [],
  ...props
}: AttributeSplitResourceFieldSelectProps) => {
  const { field } = nodeData;

  return (
    <ResourceSelect
      {...props}
      placeholder={displayValue ? getESFieldDisplayName(displayValue, dataType) : props.placeholder}
      value={field}
      data-selectoptiontype="profileFields"
      options={fieldOptions}
      isLoading={isLoadingMapping}
    />
  );
};

export const ResourceFieldSelect = ({
  nodeData = {},
  dataType,
  displayValue,
  isStartNode = false,
  ...props
}: ResourceFieldSelectProps) => {
  const { field } = nodeData;
  const [fieldOptions, isLoadingMapping] = useFieldMappingOptions({ dataType, isStartNode });
  const filteredOptions = useMemo(
    () =>
      props.id === 'trigger-data'
        ? fieldOptions.filter((option) => excludedOptions.indexOf(option.value) === -1)
        : fieldOptions,
    [fieldOptions, props.id],
  );

  return (
    <ResourceSelect
      {...props}
      placeholder={displayValue ? getESFieldDisplayName(displayValue, dataType) : props.placeholder}
      value={field}
      data-selectoptiontype="profileFields"
      // if in attribute split do not use filtered values, if in trigger filter excluded options
      options={filteredOptions}
      isLoading={!!isLoadingMapping}
    />
  );
};

export default ResourceFieldSelect;
